/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import PageWrapper from 'components/pageWrapper';
import styles from 'styles/pages/insights.module.scss';
import Header from 'components/header';
import DataVignetteWrapper from 'components/dataVignetteWrapper';
import {
  Alert,
  Avatar,
  Button,
  Divider,
  Empty,
  Pagination,
  Radio,
  Table,
  Tag,
} from 'antd';
import Pie from 'components/charts/pie';
import Line from 'components/charts/line';
import { fetchInsights, getDepartments } from 'utils/api';
import {
  INSIGHT_TIME_FILTERS,
  INSIGHT_TYPES,
  RANKING_PAGE_SIZE,
  SEARCH_PARAMS_NAME,
  USER_RANKINGS,
} from 'utils/constants';
import {
  slugify,
  transformActiveUsersData,
  transformCompanyValues,
  transformDataForLine,
  transformClapsExchangedData,
  transformTopUsersPerCompanyValues,
  useWindowSize,
  isSuperAdmin,
} from 'utils/utils';
import Ranking from 'components/charts/ranking';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { InfoCircleFilled, TrophyTwoTone } from '@ant-design/icons';
import ProfileContext from 'components/profileContext';
import { shortnamesToUnicode } from 'emojitsu';
import SelectDepartments from 'components/selectDepartments';

const Insights = () => {
  const [rawData, setRawData] = useState(null);
  const [companyValues, setCompanyValues] = useState(null);
  const [topUsersCompanyValues, setTopUsersCompanyValues] = useState(null);
  const [activeUsers, setActiveUsers] = useState(null);
  const [clapsExchanged, setClapsExchanged] = useState(null);
  const [deptInsights, setDeptInsights] = useState(null);
  const [pageReceiver, setPageReceiver] = useState(1);
  const [pageGiver, setPageGiver] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const size = useWindowSize();
  const { profile } = useContext(ProfileContext);
  const navigate = useNavigate();
  const [timeFilter, setTimeFilter] = useState(
    searchParams.get('TIME_FILTER') || INSIGHT_TIME_FILTERS.ALL_TIME.id
  );
  const [departmentFilter, setDepartmentFilter] = useState(
    searchParams.get('DEPARTMENT') || null
  );
  const [departments, setDepartments] = useState(null);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const fetch = (filter) => {
    return fetchInsights(
      pageGiver,
      pageReceiver,
      filter,
      departments && departments.status === 'LIVE' ? true : false,
      departmentFilter
    ).then((data) => {
      const formatedData = data.reduce(
        (acc, element) => ({
          ...acc,
          [element.type]:
            element.params && element.params.type
              ? { ...acc[element.type], [element.params.type]: element.data }
              : element.data,
        }),
        {}
      );

      setRawData(formatedData);
      setCompanyValues(
        transformCompanyValues(
          formatedData[INSIGHT_TYPES.VALUES_USAGE],
          profile.companyValues
        )
      );
      setTopUsersCompanyValues(
        transformTopUsersPerCompanyValues(
          formatedData[INSIGHT_TYPES.TOP_USER_PER_VALUE]
        )
      );
      setActiveUsers(
        transformDataForLine(
          transformActiveUsersData(
            formatedData[INSIGHT_TYPES.ACTIVE_USERS_30D]
          ),
          'users'
        )
      );
      setClapsExchanged(
        transformDataForLine(
          transformClapsExchangedData(
            formatedData[INSIGHT_TYPES.CLAPS_EXCHANGED_LAST_WEEKS]
          ),
          'claps'
        )
      );
      setDeptInsights(formatedData[INSIGHT_TYPES.DEPT_INSIGHTS]);
      setPageGiver(searchParams.get(SEARCH_PARAMS_NAME.PAGE_GIVER) || 1);
      setPageReceiver(searchParams.get(SEARCH_PARAMS_NAME.PAGE_RECEIVER) || 1);
      setLoading(false);
    });
  };

  const fetchDepartments = () => {
    setLoadingDepartments(true);
    getDepartments()
      .then((data) => {
        setDepartments(data);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        setLoadingDepartments(false);
      });
  };

  useEffect(() => {
    if (!profile || !profile.isAdmin) {
      navigate('/');
    } else {
      fetch(timeFilter);
      fetchDepartments();
    }
  }, [pageGiver, pageReceiver]);

  useEffect(() => {
    const timeFilterGet = searchParams.get('TIME_FILTER');
    const departmentGet = searchParams.get('DEPARTMENT');
    setDepartmentFilter(departmentGet);
    if (timeFilterGet) {
      setTimeFilter(timeFilterGet);
    }
  }, [searchParams]);

  useEffect(() => {
    fetch(timeFilter);
  }, [departmentFilter, timeFilter, departments]);

  const onChangePageGiver = (newPage) => {
    searchParams.set(SEARCH_PARAMS_NAME.PAGE_GIVER, newPage);
    setPageGiver(newPage);
    setSearchParams(searchParams);
  };
  const onChangePageReceiver = (newPage) => {
    searchParams.set(SEARCH_PARAMS_NAME.PAGE_RECEIVER, newPage);
    setPageReceiver(newPage);
    setSearchParams(searchParams);
  };

  const usageDataDepartment = deptInsights
    ?.map((dep) => ({
      _id: dep.departmentId,
      color: departments.departments.find((d) => d._id === dep.departmentId)
        .color,
      name: departments.departments.find((d) => d._id === dep.departmentId)
        .value,
      usage: parseFloat(
        ((dep.insights.activeUsers / dep.insights.totalUsers) * 100).toFixed(0)
      ),
    }))
    .sort((a, b) => b.usage - a.usage);

  const kudosPerUserDepartment = deptInsights
    ?.map((dep) => ({
      _id: dep.departmentId,
      color: departments.departments.find((d) => d._id === dep.departmentId)
        .color,
      name: departments.departments.find((d) => d._id === dep.departmentId)
        .value,
      usage:
        dep.insights.claps > 0
          ? parseFloat(
              (dep.insights.totalUsers / dep.insights.claps).toFixed(0)
            )
          : null,
    }))
    .sort((a, b) => b.usage - a.usage);

  const currentDepartment =
    departments?.departments?.find((d) => d._id === departmentFilter) || null;


  return (
    <>
      <Header />
      <PageWrapper>
        <div className={styles.titleWrapper}>
          <div className={styles.titleContent}>
            <div className={styles.titleContentLeft}>
              <h1 className={styles.title}>Insights</h1>
              <div className={styles.subtitle}>
                Get analytics about your employee's engagement and Clappy's
                usage
              </div>
            </div>
          </div>
          <div className={styles.titleContentRight}>
            {departments && departments.status === 'LIVE' ? (
              <a
                href={departments.link}
                style={{ textDecoration: 'none' }}
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  type="link"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <img
                    src="/img/logo/linky.png"
                    width={22}
                    height={22}
                    alt="linky"
                  />
                  &nbsp;
                  <span style={{ textDecoration: 'none' }}>Open Linky</span>
                </Button>
              </a>
            ) : (
              <></>
            )}
            <SelectDepartments
              departments={departments}
              loading={loadingDepartments}
              size="default"
            />
            <Radio.Group
              options={Object.values(INSIGHT_TIME_FILTERS).map((filter) => ({
                label: filter.label,
                value: filter.id,
              }))}
              onChange={(e) => {
                searchParams.set('TIME_FILTER', e.target.value);
                setSearchParams(searchParams);
              }}
              value={timeFilter}
              optionType="button"
              buttonStyle="solid"
            />
          </div>
          <div className={styles.wrapper}>
            {/* <div className={styles.vignettes}>
                <NumberVignette
                  background="#d5eeff"
                  value={
                    rawData &&
                    rawData[INSIGHT_TYPES.CLAPS_EXCHANGED_ALL_TIME] &&
                    !loading
                      ? rawData[INSIGHT_TYPES.CLAPS_EXCHANGED_ALL_TIME]
                      : "-"
                  }
                  label={"All-time claps exchanged "}
                />
              </div> */}
            {departments &&
            departments.status === 'LIVE' &&
            deptInsights &&
            deptInsights.length > 0 ? (
              <div className={styles.dataWrapper}>
                <DataVignetteWrapper
                  // large
                  title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ flexGrow: 1 }}>
                        % of active users per department
                      </div>
                      <Tag className={styles.linkyTag}>
                        <img
                          src="/img/logo/linky.png"
                          width={22}
                          height={22}
                          alt="linky"
                        />
                        &nbsp; From Linky
                      </Tag>
                    </div>
                  }
                  subtitle={'Which department has the best engagement?'}
                >
                  <Table
                    style={{ width: '100%' }}
                    className={styles.tableVignette}
                    rowClassName={(record, index) => styles.tableRowVignette}
                    rowKey={(record) => record.name}
                    ghost={true}
                    size="small"
                    pagination={false}
                    bordered={false}
                    dataSource={usageDataDepartment}
                    columns={[
                      {
                        title: 'Department',
                        dataIndex: 'name',
                        key: 'name',
                        render: (text, record) => (
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <div
                              className={styles.colorDepartment}
                              style={{ backgroundColor: record.color }}
                            ></div>
                            <a
                              onClick={() => {
                                searchParams.set('DEPARTMENT', record._id);
                                setSearchParams(searchParams);
                              }}
                            >{`${record.name}`}</a>
                          </div>
                        ),
                      },

                      {
                        title: '% active users',
                        dataIndex: 'usage',
                        key: 'usage',
                        align: 'right',
                        render: (text, record) => <b>{`${text}%`}</b>,
                      },
                    ]}
                  />
                </DataVignetteWrapper>
                <DataVignetteWrapper
                  // large
                  title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ flexGrow: 1 }}>
                        Number of kudos received
                      </div>
                      <Tag className={styles.linkyTag}>
                        <img
                          src="/img/logo/linky.png"
                          width={22}
                          height={22}
                          alt="linky"
                        />
                        &nbsp; From Linky
                      </Tag>
                    </div>
                  }
                  subtitle={'Which department receives the most kudos?'}
                >
                  <Table
                    style={{ width: '100%' }}
                    className={styles.tableVignette}
                    rowClassName={(record, index) => styles.tableRowVignette}
                    ghost={true}
                    size="small"
                    pagination={false}
                    bordered={false}
                    rowKey={(record) => record.name}
                    dataSource={kudosPerUserDepartment}
                    columns={[
                      {
                        title: 'Department',
                        dataIndex: 'name',
                        key: 'name',
                        render: (text, record) => (
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <div
                              className={styles.colorDepartment}
                              style={{ backgroundColor: record.color }}
                            ></div>
                            <a
                              onClick={() => {
                                searchParams.set('DEPARTMENT', record._id);
                                setSearchParams(searchParams);
                              }}
                            >{`${record.name}`}</a>
                          </div>
                        ),
                      },

                      {
                        title: 'Total Claps',
                        dataIndex: 'usage',
                        key: 'usage',
                        align: 'right',
                        render: (text, record) =>
                          text && text > 0 ? <b>{`${text} 👏`}</b> : '-',
                      },
                    ]}
                  />
                </DataVignetteWrapper>
              </div>
            ) : (
              <></>
            )}
            <div className={styles.dataWrapper}>
              <DataVignetteWrapper
                // large
                title={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ flexGrow: 1 }}>Company Values</div>
                    {currentDepartment && (
                      <Tag className={styles.departmentTag}>
                        <div
                          className={styles.colorDepartment}
                          style={{ backgroundColor: currentDepartment.color }}
                        ></div>
                        &nbsp; {currentDepartment.value}
                      </Tag>
                    )}
                  </div>
                }
                subtitle={'Which company values were used in recognitions'}
              >
                {rawData &&
                  !loading &&
                  (companyValues && companyValues.length > 0 ? (
                    <>
                      <div className={styles.pieWrapper}>
                        <Pie
                          data={companyValues}
                          width={size.width < 1080 ? size.width - 72 : 460}
                          {...(size.width < 780 ? { simpleLabels: true } : {})}
                          {...(companyValues.length > 10
                            ? { simpleLabels: true }
                            : {})}
                        />
                      </div>

                      <Alert
                        banner
                        type="info"
                        description="Tip: You can see this graph scoped on someone's profile by clicking on a user name in the Leaderboard section."
                        className={styles.hintAlert}
                        icon={
                          <InfoCircleFilled
                            style={{
                              color: 'grey',
                              fontSize: '16px',
                              marginTop: '4px',
                            }}
                          />
                        }
                        showIcon
                      />
                    </>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={'No company values were used yet'}
                    />
                  ))}
              </DataVignetteWrapper>
              <DataVignetteWrapper
                title={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ flexGrow: 1 }}>
                      Top employees per Company value
                    </div>
                    {currentDepartment && (
                      <Tag className={styles.departmentTag}>
                        <div
                          className={styles.colorDepartment}
                          style={{ backgroundColor: currentDepartment.color }}
                        ></div>
                        &nbsp; {currentDepartment.value}
                      </Tag>
                    )}
                  </div>
                }
                subtitle={'Discover top performers per value'}
              >
                {rawData &&
                  !loading &&
                  (companyValues && companyValues.length > 0 ? (
                    <>
                      <br />
                      {topUsersCompanyValues.map((value, i) => (
                        <div key={value.name}>
                          <div className={styles.topUsersValueName}>
                            <div className={styles.topUsersValueNameInner}>
                              {shortnamesToUnicode(value.name)}
                            </div>

                            {!value.topUser && (
                              <span className={styles.topUserNoData}>
                                Not enough data
                              </span>
                            )}
                            {value.topUser && (
                              <div>
                                <Link
                                  to={`/leaderboards?type=received&value=${slugify(
                                    value.name
                                  )}&date=${timeFilter}${
                                    departmentFilter
                                      ? `&DEPARTMENT=${departmentFilter}`
                                      : ''
                                  }`}
                                  className={styles.TopUserViewLeaderboard}
                                >
                                  View leaderboard
                                </Link>
                              </div>
                            )}
                          </div>
                          {value.topUser && (
                            <div className={styles.topUserWrapper}>
                              <Avatar
                                className={styles.topUsersAvatar}
                                src={value.topUser.profilePicture}
                                size={48}
                              />
                              <TrophyTwoTone
                                twoToneColor="orange"
                                className={styles.topUsersIcon}
                              />
                              <span className={styles.topValueUsername}>
                                <Link to={`/users/${value.topUser._id}`}>
                                  {value.topUser.name}
                                </Link>
                              </span>
                            </div>
                          )}
                          {i < topUsersCompanyValues.length - 1 && <Divider />}
                        </div>
                      ))}
                    </>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={'No company values were used yet'}
                    />
                  ))}
              </DataVignetteWrapper>
            </div>
            <Divider />
            <h2 className={styles.secondaryTitle}>Leaderboards</h2>
            <div className={styles.dataWrapper}>
              <DataVignetteWrapper
                title={
                  <div className={styles.titleWrapperVignette}>
                    <div className={styles.titleLeft}>Top Receivers</div>
                    <div className={styles.titleRight}>
                      {currentDepartment && (
                        <Tag className={styles.departmentTag}>
                          <div
                            className={styles.colorDepartment}
                            style={{ backgroundColor: currentDepartment.color }}
                          ></div>
                          &nbsp; {currentDepartment.value}
                        </Tag>
                      )}
                      <a
                        href={`/#/leaderboards?type=received&date=${timeFilter}${
                          departmentFilter
                            ? `&DEPARTMENT=${departmentFilter}`
                            : ''
                        }`}
                      >
                        View all
                      </a>
                    </div>
                  </div>
                }
                subtitle={INSIGHT_TIME_FILTERS[timeFilter]?.label}
              >
                {rawData &&
                !loading &&
                rawData[INSIGHT_TYPES.USER_RANKING][USER_RANKINGS.RECEIVER]
                  ?.users?.length > 0 ? (
                  <>
                    <Ranking
                      data={
                        rawData[INSIGHT_TYPES.USER_RANKING][
                          USER_RANKINGS.RECEIVER
                        ]
                      }
                    />
                    <div className={styles.paginate}>
                      <Pagination
                        showSizeChanger={false}
                        onChange={onChangePageReceiver}
                        current={+pageReceiver}
                        size="small"
                        defaultCurrent={
                          rawData[INSIGHT_TYPES.USER_RANKING][
                            USER_RANKINGS.RECEIVER
                          ].from /
                            RANKING_PAGE_SIZE +
                          1
                        }
                        total={
                          rawData[INSIGHT_TYPES.USER_RANKING][
                            USER_RANKINGS.RECEIVER
                          ].total
                        }
                      />
                    </div>
                  </>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={'No leaderboard to display yet'}
                  />
                )}
              </DataVignetteWrapper>
              <DataVignetteWrapper
                title={
                  <div className={styles.titleWrapperVignette}>
                    <div className={styles.titleLeft}>Top Givers</div>
                    <div className={styles.titleRight}>
                      {currentDepartment && (
                        <Tag className={styles.departmentTag}>
                          <div
                            className={styles.colorDepartment}
                            style={{ backgroundColor: currentDepartment.color }}
                          ></div>
                          &nbsp; {currentDepartment.value}
                        </Tag>
                      )}
                      <a
                        href={`/#/leaderboards?type=given&date=${timeFilter}${
                          departmentFilter
                            ? `&DEPARTMENT=${departmentFilter}`
                            : ''
                        }`}
                      >
                        View all
                      </a>
                    </div>
                  </div>
                }
                subtitle={INSIGHT_TIME_FILTERS[timeFilter].label}
              >
                {rawData &&
                !loading &&
                rawData[INSIGHT_TYPES.USER_RANKING][USER_RANKINGS.GIVER]?.users
                  ?.length > 0 ? (
                  <>
                    <Ranking
                      data={
                        rawData[INSIGHT_TYPES.USER_RANKING][USER_RANKINGS.GIVER]
                      }
                    />
                    <div className={styles.paginate}>
                      <Pagination
                        showSizeChanger={false}
                        current={+pageGiver}
                        onChange={onChangePageGiver}
                        size="small"
                        defaultCurrent={
                          rawData[INSIGHT_TYPES.USER_RANKING][
                            USER_RANKINGS.GIVER
                          ].from /
                            RANKING_PAGE_SIZE +
                          1
                        }
                        total={
                          rawData[INSIGHT_TYPES.USER_RANKING][
                            USER_RANKINGS.GIVER
                          ].total
                        }
                      />
                    </div>
                  </>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={'No leaderboard to display yet'}
                  />
                )}
              </DataVignetteWrapper>
              {isSuperAdmin(profile) ? (
                <>
                  <Divider />
                  <h2 className={styles.secondaryTitle}>Activity</h2>
                  <div className={styles.dataWrapper}>
                    <DataVignetteWrapper
                      additionalOption={{
                        title: 'List inactive employees',
                        link: '/#/inactive-employees',
                      }}
                      title={'Active employees'}
                      infoMessage="Please note that today's data will be available tomorrow"
                      subtitle={
                        'How many employees interacted with Clappy these last 30 days'
                      }
                    >
                      <Line
                        data={activeUsers}
                        name="users"
                        width={size.width < 1080 ? size.width - 70 : 450}
                      />
                    </DataVignetteWrapper>
                    <DataVignetteWrapper
                      title={'Claps Exchanged'}
                      subtitle={'How many claps were given these last 12 weeks'}
                      infoMessage="Please note that this week's data will be available next week"
                    >
                      <Line
                        data={clapsExchanged}
                        name="claps"
                        width={size.width < 1080 ? size.width - 70 : 450}
                      />
                    </DataVignetteWrapper>
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </PageWrapper>
    </>
  );
};

export default Insights;
